import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import { Oval } from "react-loader-spinner";
import PlanupdateRequest from "../model/request/PlanupdateRequest";
import ApiResponse from "../model/response/ApiResponse";
import logo from '../images/logo.jpeg';
import '../css/UpgradePage.css';
import ApiConfig from "../utils/ApiConfig";

function UpgradePage() {
    const navigation = useNavigate();
    const location = useLocation();

    const rawVal = localStorage.getItem('loginVal');
    let loginVal = rawVal ? JSON.parse(rawVal) : null;
    let token = loginVal ? loginVal.token : null;

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [selectedMembershipType, setSelectedMembershipType] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [silverPlans, setSilverPlans] = useState([]);
    const [goldPlans, setGoldPlans] = useState([]);
    const [platinumPlans, setPlatinumPlans] = useState([]);

    const cleanValue = (value) => {
        if (value === "null" || value === null || value === undefined || value === "") {
            return null;
        }
        return value;
    };

    // =========================================
    // FETCH PLANS
    // =========================================
    const fetchPlans = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await axios.get(
                ApiConfig.getUrl("api/Razorpay/plans"),
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Cache-Control': 'no-cache',
                        'Pragma': 'no-cache',
                        'Expires': '0',
                    },
                    params: { _t: Date.now() }
                }
            );

            const data = response.data;

            if (data.status && data.statusCode === 200) {
                const mapPlan = (p) => ({
                    planId: p.planId,
                    title: cleanValue(p.planTitle) || "",
                    duration: cleanValue(p.duration) || "",
                    amount: p.amount || 0,
                    save: cleanValue(p.saveOffer) || "",
                    membershipType: cleanValue(p.membershipType) || ""
                });

                setSilverPlans((data.response.silver || []).map(mapPlan));
                setGoldPlans((data.response.gold || []).map(mapPlan));
                setPlatinumPlans((data.response.platinum || []).map(mapPlan));
            } else {
                toast.error(data.message || "Failed to fetch plans");
            }
        } catch (error) {
            console.error("Fetch Plans Error:", error);
            if (error.response?.status === 401) {
                toast.error("Session expired. Please login again.");
                localStorage.clear();
                navigation("/login");
            } else {
                toast.error("Failed to load membership plans");
            }
        } finally {
            setIsLoading(false);
        }
    }, [navigation, token]);

    useEffect(() => {
        fetchPlans();

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        document.body.appendChild(script);
        return () => {
            if (document.body.contains(script)) {
                document.body.removeChild(script);
            }
        };
    }, [fetchPlans]);

    // =========================================
    // HANDLE PLAN CHANGE
    // =========================================
    const handlePlanChange = (type, index, field, value) => {
        const setterMap = {
            Silver: setSilverPlans,
            Gold: setGoldPlans,
            Platinum: setPlatinumPlans
        };

        const setPlans = setterMap[type];
        setPlans(prevPlans => {
            const newPlans = [...prevPlans];
            newPlans[index] = {
                ...newPlans[index],
                [field]: field === 'amount' ? Number(value) : value
            };
            return newPlans;
        });
    };

    // =========================================
    // PAYMENT
    // =========================================
    const handlePayNow = async (membershipType) => {
        if (!selectedPlan || selectedMembershipType !== membershipType) {
            toast.warning(`Please select a ${membershipType} plan`);
            return;
        }

        if (!window.Razorpay) {
            toast.error("Razorpay SDK not loaded");
            return;
        }

        if (!token) {
            toast.error("Please login again");
            navigation("/login");
            return;
        }

        if (isLoading) return;
        setIsLoading(true);

        const plans = membershipType === 'Silver'
            ? silverPlans
            : membershipType === 'Gold'
                ? goldPlans
                : platinumPlans;

        const latestPlan = plans.find(p => p.planId === selectedPlan.planId) || selectedPlan;

        try {
            const orderResponse = await axios.post(
                ApiConfig.getUrl("api/Razorpay/create-order"),
                {
                    user_Id: loginVal.user_Id,
                    amount: Number(latestPlan.amount),
                    planId: latestPlan.planId,
                },
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            const orderData = orderResponse.data;


            if (orderData?.asiStatus !== true) {
                toast.info(orderData?.message || "Failed to create order");
                setIsLoading(false);
                return;
            }

            const orderDetails = orderData?.response;


            if (orderDetails?.apiStatus === 2) {
                toast.info(orderDetails?.message || orderResponse.data?.message);
                setIsLoading(false);
                return;
            }

            if (!orderDetails?.id) {
                toast.error(orderResponse.data?.message || "Failed to create order");
                setIsLoading(false);
                return;
            }

            const options = {
                key: "rzp_test_TTa99SB88bTpGe",
                amount: orderDetails.amount,
                currency: orderDetails.currency || "INR",
                 created_at: orderDetails?.created_at || null,
                name: "IndiaMetti",
                description: `${membershipType} - ${latestPlan.title}`,
                order_id: orderDetails.id,
                planId: latestPlan.planId,
                prefill: {
                    name: loginVal?.user_Name || "",
                    email: loginVal?.email_Id || "",
                    contact: loginVal?.mobile_number || "",
                },
                theme: { color: "#ff8c00" },
                // inside handlePayNow -> options -> handler callback

                handler: async function (response) {
                    try {
                        const verifyResponse = await axios.post(
                            ApiConfig.getUrl("api/Razorpay/payment-success"),
                            {
                                user_Id: loginVal.user_Id,
                                paymentId: response.razorpay_payment_id,
                                entity: response.entity || "payment",
                                amount: orderDetails.amount,
                                currency: orderDetails.currency || "INR",
                                 created_at: orderDetails?.created_at || null,
                                status: "captured",
                                order_id: response.razorpay_order_id,
                                invoice_id: response.invoice_id || "",
                                international: false,
                                method: response.method || "online",
                                amount_refunded: 0,
                                refund_status: "",
                                captured: true,
                                description: `${membershipType} - ${latestPlan.title}`,
                                card_id: response.card_id || "",
                                bank: response.bank || "",
                                wallet: response.wallet || "",
                                vpa: response.vpa || "",
                                email: loginVal?.email_Id || "",
                                contact: loginVal?.mobile_number || "",
                                customer_id: response.customer_id || "",
                                token_id: response.token_id || "",
                                fee: response.fee || 0,
                                tax: response.tax || 0,
                                error_code: "",
                                error_description: "",
                                error_source: "",
                                error_step: "",
                                error_reason: "",
                                auth_code: response.acquirer_data?.auth_code || "",
                                arn: response.acquirer_data?.arn || "",
                                rrn: response.acquirer_data?.rrn || "",
                                bank_transaction_id: response.bank_transaction_id || "",
                                transaction_id: response.transaction_id || "",
                                payer_account_type: response.payer_account_type || "",
                                upi_flow: response.upi_flow || "",
                                planId: latestPlan.planId
                            },
                            {
                                headers: {
                                    "Content-Type": "application/json",
                                    Authorization: `Bearer ${token}`,
                                },
                            }
                        );

                        // API response
                        const apiResponse = verifyResponse?.data;

                        console.log("Payment Success API Response:", apiResponse);

                        // Get API message
                        const apiMessage =
                            apiResponse?.message ||
                            apiResponse?.data?.message ||
                            apiResponse?.data?.response?.message ||
                            "Payment verification failed";

                        // Get API status
                        const statusCode =
                            apiResponse?.statusCode ||
                            apiResponse?.data?.statusCode;

                        const apiStatus =
                            apiResponse?.response?.apiStatus ||
                            apiResponse?.data?.response?.apiStatus;

                        // Payment successful
                        if (
                            statusCode === 200 &&
                            (apiStatus === 1 || apiStatus === 2)
                        ) {
                            // API-la irunthu varra message direct-a toast
                            toast.success(apiMessage);

                            // Update login state
                            const updatedLoginVal = {
                                ...loginVal,
                                isPaid: true
                            };

                            localStorage.setItem(
                                "loginVal",
                                JSON.stringify(updatedLoginVal)
                            );

                            // Redirect
                            setTimeout(() => {
                                navigation("/Viewpage");
                            }, 1500);

                        } else {
                            // API failure message
                            toast.error(apiMessage);
                        }

                    } catch (error) {
                        console.error("Verification Error:", error);

                        // Error API response message
                        const errorMessage =
                            error?.response?.data?.message ||
                            error?.response?.data?.data?.message ||
                            error?.response?.data?.data?.response?.message ||
                            error?.response?.data?.errorMessage ||
                            "Verification failed";

                        toast.error(errorMessage);

                    } finally {
                        setIsLoading(false);
                    }
                },
                modal: {
                    ondismiss: function () {
                        toast.info("Payment cancelled");
                        setIsLoading(false);
                    },
                },
            };

            const rzp = new window.Razorpay(options);
            rzp.on("payment.failed", (err) => {
                console.error("Payment Failed:", err);
                toast.error(err.error?.description || "Payment Failed");
                setIsLoading(false);
            });
            rzp.open();

        } catch (error) {
            console.error("Payment Error:", error);
            toast.error("Unable to process payment");
            setIsLoading(false);
        }
    };

    // =========================================
    // BULK UPDATE PLANS
    // =========================================
    const handleFinishEditing = async () => {
        setIsLoading(true);
        const allPlans = [...silverPlans, ...goldPlans, ...platinumPlans];

        try {
            await Promise.all(
                allPlans.map(plan => {
                    const request = new PlanupdateRequest();
                    request.planId = plan.planId;
                    request.membershipType = plan.membershipType;
                    request.planTitle = plan.title;
                    request.duration = plan.duration;
                    request.amount = Number(plan.amount);
                    request.saveOffer = plan.save;

                    return axios.put(
                        ApiConfig.getUrl("api/Razorpay/updateplans"),
                        { request: request },
                        {
                            headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                            },
                        }
                    );
                })
            );

            toast.success("Plans updated successfully");
            setIsEditing(false);
            await fetchPlans();
        } catch (error) {
            console.error("Bulk Plan Update Error:", error);
            toast.error("Failed to update plans. Please check the logs.");
        } finally {
            setIsLoading(false);
        }
    };

    // =========================================
    // PLAN CARD RENDER
    // =========================================
    const renderPlans = (plans, type, themeClass) => (
        <div className="col-xl-3 col-lg-4 col-md-6 mb-4 membership-card center">
            <div className={themeClass}>
                <div className="membership-badge">
                    {type.toUpperCase()} MEMBERSHIP
                </div>
                <h2 className='my-5 text-dark'>{type}</h2>

                <div className="inline-plan-options">
                    {plans.map((plan, index) => (
                        <div
                            key={plan.planId || index}
                            className={`popup-plan-card ${!isEditing &&
                                selectedPlan?.planId === plan.planId &&
                                selectedMembershipType === type
                                ? 'active-plan'
                                : ''
                                }`}
                            onClick={() => {
                                if (isEditing) return;
                                setSelectedPlan(plan);
                                setSelectedMembershipType(type);
                            }}
                        >
                            <div className="save-tag">
                                {isEditing ? (
                                    <input
                                        className="form-control form-control-sm text-center bg-transparent border-0 text-white p-0"
                                        value={plan.save}
                                        onChange={(e) => handlePlanChange(type, index, 'save', e.target.value)}
                                    />
                                ) : plan.save}
                            </div>

                            {isEditing ? (
                                <div className="p-1">
                                    <input className="form-control form-control-sm mb-1" value={plan.title} onChange={(e) => handlePlanChange(type, index, 'title', e.target.value)} />
                                    <input className="form-control form-control-sm mb-1" value={plan.duration} onChange={(e) => handlePlanChange(type, index, 'duration', e.target.value)} />
                                    <input className="form-control form-control-sm" type="number" value={plan.amount} onChange={(e) => handlePlanChange(type, index, 'amount', e.target.value)} />
                                </div>
                            ) : (
                                <>
                                    <h4>{plan.title}</h4>
                                    <p>{plan.duration}</p>
                                    <h2>₹{plan.amount}</h2>
                                </>
                            )}
                        </div>
                    ))}
                </div>

                {!isEditing && (
                    <button
                        className="pay-btn mt-3"
                        onClick={() => handlePayNow(type)}
                    >
                        Pay Now
                    </button>
                )}
            </div>
        </div>
    );

    return (
        <>
            <div className="fullview">
                <ToastContainer />

                {isLoading && (
                    <div className="loader-overlay">
                        <Oval
                            height={60}
                            width={60}
                            color="orange"
                            secondaryColor="#cccccc"
                            strokeWidth={4}
                            visible={true}
                        />
                    </div>
                )}

                {/* NAVBAR */}
                <div className="top-bar">
                    <div className="container nav-content">
                        <div className="logo-section">
                            <img
                                src={logo}
                                alt="logo"
                                className="site-logo"
                                onClick={() => navigation('/Viewpage')}
                            />
                            <div>
                                <h4 className="brand-title">IndiaMetti</h4>
                                <span className="brand-subtitle">Find Your Perfect Life Partner</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* MEMBERSHIP */}
                <div className="container py-5 mt-5">
                    {loginVal?.role === 'Admin' && (
                        <div className="text-end mb-4">
                            {!isEditing ? (
                                <button
                                    className="btn btn-warning fw-bold px-4 shadow-sm"
                                    onClick={() => setIsEditing(true)}
                                >
                                    ✎ Edit Plans
                                </button>
                            ) : (
                                <div className="btn-group shadow-sm">
                                    <button
                                        className="btn btn-success fw-bold"
                                        onClick={handleFinishEditing}
                                    >
                                        ✔ Finish
                                    </button>
                                    <button
                                        className="btn btn-secondary fw-bold"
                                        onClick={() => {
                                            fetchPlans();
                                            setIsEditing(false);
                                        }}
                                    >
                                        ✖ Cancel
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    <div className="row justify-content-center">
                        {renderPlans(silverPlans, 'Silver', 'silver-theme')}
                        {renderPlans(goldPlans, 'Gold', 'gold-theme')}
                        {renderPlans(platinumPlans, 'Platinum', 'platinum-theme')}
                    </div>
                </div>
            </div>

            <footer className='bg-dark text-white text-center'>
                <label style={{ fontSize: "12px" }}>
                    Copyright © 2026 NavGraph Technologies. All Rights Reserved.
                </label>
            </footer>
        </>
    );
}

export default UpgradePage;